/* Cycling Upgrades — website chat (snippets/cu-chat.liquid). The only file that calls
   /apps/garage/chat/* (the Garage app proxy to the CRM); the browser keeps just the
   session id in localStorage. What the panel shows comes from cu-chat-core.js (unit-tested). */
const chatRoot = document.querySelector('[data-cu-chat]');
// The core module's URL comes from asset_url (with Shopify's ?v= cache-buster),
// so a deploy never pairs this file with a stale cached core.
const core = chatRoot ? await import(chatRoot.dataset.core) : null;
if (chatRoot) init(chatRoot);

async function init(root) {
  const { initialState, reduce, view, canSend, keepPolling, pollCadence, storedSession, launcherLabel, applyConfig, attachmentsOf, checkFiles, awayNote, FILE_ACCEPT } = core;
  const API = '/apps/garage/chat';
  const KEY = 'cu-chat';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const $ = (sel) => root.querySelector(sel);
  const launcher = $('[data-cu-chat-open]');
  const badge = $('[data-cu-chat-unread]');
  const labelEl = $('[data-cu-chat-label]');
  const intro = $('[data-cu-chat-greeting]');
  const greetingEl = $('[data-cu-chat-greeting-text]');
  const teaser = $('[data-cu-chat-teaser]');
  const panel = $('#cu-chat-panel');
  const closeBtn = $('[data-cu-chat-close]');
  const identify = $('[data-cu-chat-identify]');
  const thread = $('[data-cu-chat-thread]');
  const note = $('[data-cu-chat-note]');
  const compose = $('[data-cu-chat-compose]');
  const area = compose.querySelector('textarea');
  const sendBtn = compose.querySelector('button[type="submit"]');
  const attachBtn = $('[data-cu-chat-attach]');
  const fileInput = $('[data-cu-chat-files]');
  const stagedEl = $('[data-cu-chat-staged]');
  fileInput.accept = FILE_ACCEPT;

  const COPY = {
    waiting: 'Someone will reply here in a moment.',
    email: (s) => `We'll reply by email at ${s.email || 'the address you gave'}.`,
    limited: 'Slow down a little. Try again in a minute.',
    unavailable: 'Chat is unavailable right now. Please email us instead.',
    failed: "That didn't send. Your message is still in the box; try again.",
    uploadFailed: "A file didn't upload. It's still attached; try again.",
    uploading: 'Sending your files…',
    firstText: 'Type a message to start the chat; your files go with it.',
  };

  let state = initialState({ email: root.dataset.email, name: root.dataset.name });
  let unavailable = false; // 401/403/503: proxy signature refused or chat off on the CRM side; stop calling it
  let sendError = '';
  let busy = false;
  let staged = []; // File objects picked for the next message (kept out of the pure state)

  // The CRM decides whether chat shows and what it says. The snippet's text is only the
  // placeholder; any failure (403 off, 503, network) leaves chat hidden. Plain fetch, not
  // api(): its 404/429/403 handling dispatches into the thread state.
  const cfg = await fetch(API + '/config', { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null)).catch(() => null);
  state = applyConfig({ ...state, greeting: greetingEl.textContent.trim(), launcherLabel: labelEl.textContent.trim() }, cfg);
  if (!state.enabled) { console.warn('[cu-chat] chat is off'); return; }
  greetingEl.textContent = state.greeting;
  $('[data-cu-chat-teaser-text]').textContent = state.greeting;
  labelEl.textContent = state.launcherLabel;

  // savedAt is the last chat activity (a message either way), not the last poll, so an
  // idle thread left open in a tab still expires after SESSION_TTL_MS.
  let activeAt = Date.now();
  try {
    // since stays null so the first poll restores the whole thread; an expired session is dropped.
    const saved = JSON.parse(localStorage.getItem(KEY) || 'null');
    const session = storedSession(saved, Date.now());
    if (session) { state = { ...state, session, phase: 'thread' }; activeAt = saved.savedAt; }
    else localStorage.removeItem(KEY);
  } catch {}
  const persist = () => {
    try {
      if (state.session) localStorage.setItem(KEY, JSON.stringify({ session: state.session, since: state.since, savedAt: activeAt }));
      else localStorage.removeItem(KEY);
    } catch {}
  };
  const dispatch = (e) => {
    // Activity: the visitor sends, or a new staff reply lands (not the first poll, which replays the thread).
    if (e.type === 'sent' || (e.type === 'poll' && state.since && core.newStaffCount(state.messages, e.messages || []))) activeAt = Date.now();
    state = reduce(state, e);
    persist(); render();
  };

  async function api(path, payload) {
    const r = await fetch(API + path, payload
      ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload) }
      : { headers: { Accept: 'application/json' } });
    const j = await r.json().catch(() => ({}));
    if (r.status === 404) { dispatch({ type: 'unknown' }); throw new Error('unknown'); }
    if (r.status === 429) { dispatch({ type: 'limited', now: Date.now() }); throw new Error('limited'); }
    if (r.status === 401 || r.status === 403 || r.status === 503) { unavailable = true; clearTimeout(timer); render(); throw new Error('unavailable'); }
    if (!r.ok) { const err = new Error(j.error || 'failed'); err.detail = typeof j.message === 'string' ? j.message : ''; throw err; }
    return j;
  }

  // Thread: append new bubbles and re-key the local echo when its server row lands,
  // so the polite live region announces each message once.
  function bubble(m) {
    const el = document.createElement('p');
    el.className = `cu-chat__msg cu-chat__msg--${m.direction === 'out' ? 'staff' : 'you'}`;
    const who = document.createElement('span');
    who.className = 'cu-visually-hidden';
    who.textContent = m.direction === 'out' ? 'Cycling Upgrades: ' : 'You: ';
    const text = document.createElement('span');
    text.dataset.text = '';
    text.textContent = m.text;
    el.append(who, text);
    if (m.pending) el.append(pendingFiles(m.pending));
    el.dataset.id = m.id;
    return el;
  }
  // The visitor's own files while they upload: names only, replaced by links when the server row lands.
  function pendingFiles(names) {
    const box = document.createElement('span');
    box.className = 'cu-chat__files';
    box.dataset.pending = '';
    for (const n of names) {
      const f = document.createElement('span');
      f.className = 'cu-chat__file cu-chat__file--pending';
      f.textContent = n;
      box.append(f);
    }
    return box;
  }
  // Files on a message (staff's, or the visitor's own), under its text: an image as a small thumbnail
  // linking to the full image, anything else as a link with its name. DOM APIs only;
  // every link comes through attachmentsOf (https only).
  function fileLinks(m) {
    const box = document.createElement('span');
    box.className = 'cu-chat__files';
    for (const f of attachmentsOf(m)) {
      const a = document.createElement('a');
      a.className = f.isImage ? 'cu-chat__file cu-chat__file--image' : 'cu-chat__file';
      a.href = f.url;
      a.target = '_blank';
      a.rel = 'noopener';
      if (f.isImage) {
        const img = document.createElement('img');
        img.src = f.url;
        img.alt = f.name;
        img.loading = 'lazy';
        img.decoding = 'async';
        a.append(img);
      } else {
        a.textContent = f.name;
      }
      box.append(a);
    }
    return box;
  }
  // Each poll re-signs the links (they last a day): a later copy of a reply adds files
  // that arrived late and points existing links at the fresh URLs. A copy with none
  // (signing failed on that poll) leaves what is shown alone.
  function syncFiles(el, m) {
    const files = attachmentsOf(m);
    const box = el.querySelector('.cu-chat__files');
    // A local echo's names give way to the server row's links (or go, if that row has none).
    if (box && 'pending' in box.dataset && !m.pending) { if (files.length) box.replaceWith(fileLinks(m)); else box.remove(); return; }
    if (!files.length) return;
    if (!box) { el.append(fileLinks(m)); return; }
    if (box.children.length !== files.length) { box.replaceWith(fileLinks(m)); return; }
    files.forEach((f, i) => { box.children[i].href = f.url; });
  }
  // The intro bubble is pinned as the thread's first child; renderThread manages the rest.
  function renderThread() {
    const existing = [...thread.children].filter((el) => el !== intro);
    const byId = new Map(existing.map((el) => [el.dataset.id, el]));
    const used = new Set();
    const els = state.messages.map((m) => {
      let el = byId.get(m.id);
      if (!el && m.direction === 'in') {
        el = existing.find((x) => !used.has(x) && x.dataset.id.startsWith('local:') && !state.messages.some((y) => y.id === x.dataset.id) && x.querySelector('[data-text]').textContent === m.text);
        if (el) el.dataset.id = m.id;
      }
      el = el || bubble(m);
      syncFiles(el, m);
      used.add(el);
      return el;
    });
    const prefix = els.slice(0, existing.length);
    const added = els.length > existing.length;
    if (prefix.length === existing.length && prefix.every((el, i) => el === existing[i])) {
      if (added) thread.append(...els.slice(existing.length));
    } else {
      thread.replaceChildren(intro, ...els);
    }
    if (added) thread.scrollTo({ top: thread.scrollHeight, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  // Picked files as chips above the box, each with a remove button (the panel's close icon).
  function renderStaged() {
    const names = staged.map((f) => f.name).join('\n');
    if (stagedEl.dataset.names === names) return;
    stagedEl.dataset.names = names;
    stagedEl.replaceChildren(...staged.map((f, i) => {
      const li = document.createElement('li');
      li.className = 'cu-chat__chip';
      const name = document.createElement('span');
      name.className = 'cu-chat__chip-name';
      name.textContent = f.name;
      const rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'cu-chat__chip-remove';
      rm.setAttribute('aria-label', `Remove ${f.name}`);
      const icon = closeBtn.querySelector('svg').cloneNode(true);
      icon.setAttribute('width', '14'); icon.setAttribute('height', '14');
      rm.append(icon);
      rm.addEventListener('click', () => { staged = staged.filter((_, j) => j !== i); sendError = ''; render(); area.focus(); });
      li.append(name, rm);
      return li;
    }));
    stagedEl.hidden = staged.length === 0;
  }

  let uploading = false;
  let limitTimer = null;
  function render() {
    const now = Date.now();
    const v = view(state, now);
    const open = state.panel === 'open';
    root.hidden = false;
    panel.hidden = !open;
    document.documentElement.toggleAttribute('data-cu-chat-open', open); // cu.css hides the Shopify Forms tab under it
    launcher.setAttribute('aria-expanded', String(open));
    badge.textContent = state.unread > 0 ? String(state.unread) : '';
    badge.hidden = state.unread === 0;
    if (state.unread > 0) launcher.setAttribute('aria-label', launcherLabel(state.launcherLabel, state.unread));
    else launcher.removeAttribute('aria-label');
    identify.hidden = state.phase !== 'identify' || unavailable;
    compose.hidden = state.phase !== 'thread' || unavailable;
    sendBtn.disabled = !canSend(state, now) || busy;
    attachBtn.disabled = !canSend(state, now) || busy;
    area.disabled = v === 'limited';
    area.required = staged.length === 0; // files alone are a message
    renderStaged();
    const away = awayNote(state, v);
    const msg = unavailable ? COPY.unavailable
      : v === 'limited' ? COPY.limited
      : sendError ? sendError
      : uploading ? COPY.uploading
      : away ? away
      : v === 'waiting' ? COPY.waiting
      : v === 'email' ? COPY.email(state)
      : '';
    if (note.textContent !== msg) note.textContent = msg;
    renderThread();
    clearTimeout(limitTimer);
    if (v === 'limited') limitTimer = setTimeout(render, state.limitedUntil - now + 50);
  }

  // Polling (cadence and when to stop: keepPolling/pollCadence in the core). Only an active
  // poll (panel open, tab visible) sends seen=1, which is what marks replies delivered and the
  // visitor present on the CRM; background polls just fetch, so a reply read in a closed panel
  // still goes by email and /close's backdate holds.
  let timer = null;
  let failures = 0;
  let inflight = false;
  let rerun = false;
  const active = () => state.panel === 'open' && document.visibilityState === 'visible';
  async function poll() {
    if (!state.session || unavailable) return;
    if (inflight) { rerun = true; return; } // e.g. opened mid-poll: run again (with seen=1) right after
    inflight = true;
    clearTimeout(timer);
    try {
      const q = { session: state.session, since: state.since || '' };
      if (active()) q.seen = '1';
      const j = await api(`/poll?${new URLSearchParams(q)}`);
      failures = 0;
      dispatch({ type: 'poll', messages: j.messages || [], status: j.status, handoff: j.handoff, since: j.since, open: j.open, hours: j.hours });
    } catch (err) {
      if (err.message !== 'unknown') failures += 1;
    }
    inflight = false;
    if (rerun) { rerun = false; poll(); } else schedule();
  }
  function schedule() {
    clearTimeout(timer);
    if (inflight || !state.session || unavailable || !keepPolling(state, Date.now(), active())) return;
    timer = setTimeout(poll, pollCadence(active(), failures));
  }
  // Back on the tab with the panel open: catch up now (seen=1); otherwise re-pick the cadence.
  document.addEventListener('visibilitychange', () => (active() ? poll() : schedule()));

  const focusables = () => [...panel.querySelectorAll('button, input:not([type="file"]), textarea')]
    .filter((el) => !el.disabled && !el.closest('[hidden]'));
  // First open with no conversation yet: a moment of "typing…" before the intro lands,
  // so it reads like someone saying hello. Skipped for reduced motion and restored threads.
  let introShown = !!state.session || reduceMotion;
  function playIntro() {
    if (introShown) return;
    introShown = true;
    intro.classList.add('is-typing');
    setTimeout(() => intro.classList.remove('is-typing'), 900);
  }
  function openPanel() {
    hideTeaser(true);
    playIntro();
    dispatch({ type: 'open' });
    poll(); // always once on open, then the open cadence
    // Focus the field to fill in; if it's hidden or disabled, the close button, so Escape still works.
    const field = identify.hidden ? area : identify.querySelector('input');
    (field.disabled || field.closest('[hidden]') ? closeBtn : field).focus();
  }
  function closePanel() {
    dispatch({ type: 'close' });
    schedule();
    launcher.focus();
  }
  launcher.addEventListener('click', () => (state.panel === 'open' ? closePanel() : openPanel()));
  closeBtn.addEventListener('click', () => {
    // The visitor closed the chat on purpose: tell the CRM so an unread reply goes by email sooner.
    if (state.session && !unavailable) {
      fetch(`${API}/close`, {
        method: 'POST', keepalive: true,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ session: state.session }),
      }).catch(() => {});
    }
    closePanel();
  });
  panel.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { e.preventDefault(); closePanel(); return; }
    if (e.key !== 'Tab') return;
    // Keep Tab inside the open panel; Escape is the way out.
    const els = focusables();
    if (!els.length) return;
    const first = els[0], last = els[els.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  identify.addEventListener('submit', (e) => {
    e.preventDefault();
    const f = new FormData(identify);
    dispatch({ type: 'identify', name: String(f.get('name') || ''), email: String(f.get('email') || '') });
    area.focus();
  });

  area.addEventListener('keydown', (e) => {
    // Enter sends; Shift+Enter starts a new line.
    if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) { e.preventDefault(); compose.requestSubmit(); }
  });

  attachBtn.addEventListener('click', () => fileInput.click());
  fileInput.addEventListener('change', () => {
    const next = [...staged, ...fileInput.files];
    fileInput.value = '';
    const ok = checkFiles(next);
    sendError = ok.ok ? '' : ok.error;
    if (ok.ok) staged = next;
    render();
    area.focus();
  });

  // Files go straight to storage: /upload hands back a signed link per file (scoped to this
  // chat), the browser PUTs each one there, then /message names them and the CRM checks the bytes.
  async function sendMessage(text, files) {
    let attachments;
    if (files.length) {
      uploading = true; render();
      try {
        const up = await api('/upload', { session: state.session, files: files.map((f) => ({ name: f.name, size: f.size, type: f.type })) });
        await Promise.all(up.uploads.map((u, i) => fetch(u.url, { method: 'PUT', headers: { 'Content-Type': u.contentType }, body: files[i] })
          .then((r) => { if (!r.ok) throw Object.assign(new Error('upload'), { detail: COPY.uploadFailed }); })
          .catch((err) => { throw err.detail ? err : Object.assign(new Error('upload'), { detail: COPY.uploadFailed }); })));
        attachments = up.uploads.map((u, i) => ({ path: u.path, name: files[i].name }));
      } finally {
        uploading = false;
      }
    }
    await api('/message', { session: state.session, message: text, attachments });
  }

  compose.addEventListener('submit', async (e) => {
    e.preventDefault();
    const text = area.value.trim();
    const files = staged;
    if ((!text && !files.length) || busy || !canSend(state, Date.now())) return;
    // /start opens the chat with text; files follow as the next message.
    if (!state.session && !text) { sendError = COPY.firstText; render(); return; }
    const at = new Date().toISOString();
    const names = files.map((f) => f.name);
    busy = true;
    sendError = '';
    compose.reset();
    staged = [];
    const echoes = []; // local echoes not yet confirmed by the server
    let textSent = false;
    try {
      if (!state.session) {
        dispatch({ type: 'sent', text, at }); echoes.push(at);
        const j = await api('/start', {
          name: state.name, email: state.email, message: text,
          pageUrl: location.href, pageTitle: root.dataset.pageTitle,
          productHandle: root.dataset.productHandle || undefined,
        });
        dispatch({ type: 'started', session: j.session, conversationId: j.conversationId });
        echoes.length = 0; textSent = true;
        if (files.length) {
          const fileAt = new Date(Date.parse(at) + 1).toISOString();
          dispatch({ type: 'sent', text: '', at: fileAt, files: names }); echoes.push(fileAt);
          await sendMessage('', files);
        }
      } else {
        dispatch({ type: 'sent', text, at, files: names }); echoes.push(at);
        await sendMessage(text, files);
      }
      busy = false;
      render();
      poll();
    } catch (err) {
      // Spec: a failed send keeps the text (and files) in the composer so the visitor can retry.
      busy = false;
      for (const a of echoes) dispatch({ type: 'unsent', at: a });
      if (!textSent) area.value = text;
      staged = files;
      if (err.detail) sendError = err.detail;
      else if (err.message !== 'limited' && err.message !== 'unavailable') sendError = COPY.failed;
      render();
    }
  });

  // Teaser: the intro pops up beside the launcher a few seconds in, once per browser
  // session, for visitors with no conversation yet. Clicking it opens the chat.
  // Not on the sell flow's page, where it would sit over the form.
  const TEASER_KEY = 'cu-chat-teaser';
  let teaserTimer = null;
  function hideTeaser(dismissed) {
    clearTimeout(teaserTimer);
    teaser.hidden = true;
    if (dismissed) try { sessionStorage.setItem(TEASER_KEY, '1'); } catch {}
  }
  let teaserSeen = false;
  try { teaserSeen = sessionStorage.getItem(TEASER_KEY) === '1'; } catch {}
  if (!state.session && !teaserSeen && !document.querySelector('[data-cu-sell]')) {
    teaserTimer = setTimeout(() => { if (state.panel !== 'open' && !unavailable) teaser.hidden = false; }, 5000);
  }
  $('[data-cu-chat-teaser-open]').addEventListener('click', openPanel);
  $('[data-cu-chat-teaser-close]').addEventListener('click', () => hideTeaser(true));

  render();
  if (state.session) poll();
}
